import { useEffect, useState } from 'react';

import { Database01Icon } from '@hugeicons/core-free-icons';
import { CandidateCheckScreens } from 'constants/candidate_screens';
import { buildNavPath as buildNavPathHelper } from 'helpers/candidate_application/form-page-helpers';
import { now } from 'helpers/date';
import { useQueryParams } from 'hooks/query-params';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import { useUserAgent } from 'hooks/user-agent';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { shouldSubmitCandidate } from 'utils/checks-utils';

import { Form } from 'components/Form/Form/Form';
import { FormMessage } from 'components/FormComponents';
import { MessageType } from 'components/FormComponents';
import StatusPill from 'components/Pills/StatusPills';

import {
  saveCheckFormAnswers,
  selectCandidateApplication,
  submitCandidateApplication,
  updateCandidateCheck,
} from 'api/candidate-application-slice';

import { IconComponent } from './../../../components/Icon/IconComponent';
import styles from './CandidateQuestionnaire.module.scss';

const CandidateQuestionnaire = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user_agent } = useUserAgent();
  const { token } = useParams();

  const [subCheckId] = useQueryParams(['subCheckId']);
  const [expirationDateParam] = useQueryParams(['expirationDate']);
  const [admin] = useQueryParams(['admin']);

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);

  const {
    application: { candidate, candidate_checks, brand, check_types, forms, company_name },
    fetched,
  } = useSelector(selectCandidateApplication);

  const { getUpload, signUpload, createUpload, deleteUpload } =
    useCandidateApplicationUploadActions(token);

  const [loading, setLoading] = useState(false);
  const [questionnaireCheck, setQuestionnaireCheck] = useState();
  const [form, setForm] = useState();
  const [subCheck, setSubCheck] = useState();

  const dueDate = subCheck?.details?.due_date || expirationDateParam;

  useEffect(() => {
    if (!fetched) return;
    const questionnaireChecks = (candidate_checks || []).filter(
      c => c.type === 'candidate_questionnaire',
    );
    const parentCheck = subCheckId
      ? questionnaireChecks.find(qc => (qc.sub_checks || []).some(sc => sc.id == subCheckId))
      : questionnaireChecks[0];
    setQuestionnaireCheck(parentCheck);
    if (subCheckId) {
      const foundSubCheck = parentCheck?.sub_checks?.find(sc => sc.id == subCheckId);
      setSubCheck(foundSubCheck);
      const formId = foundSubCheck?.details?.id;
      setForm((forms || [])?.find(form => form.id === formId));
    } else {
      const formId = parentCheck?.details?.form_id;
      setForm((forms || [])?.find(form => form.id === formId));
    }
  }, [fetched, subCheckId, candidate_checks, forms]);

  const onSubmit = async data => {
    setLoading(true);
    const { payload: updatedCheck } = await dispatch(
      saveCheckFormAnswers({
        token,
        id: questionnaireCheck.id,
        params: { answers: data, user_agent, sub_check_id: subCheckId },
      }),
    );

    const unsubmittedQuestionnaires = updatedCheck.sub_checks?.filter(
      subCheck => !subCheck.completed_at,
    );

    if (unsubmittedQuestionnaires?.length < 1) {
      await dispatch(
        updateCandidateCheck({ token, id: updatedCheck.id, data: { submitted_at: now() } }),
      );
    }

    if (
      unsubmittedQuestionnaires?.length < 1 &&
      shouldSubmitCandidate(updatedCheck, candidate_checks, check_types)
    ) {
      await dispatch(submitCandidateApplication({ token, user_agent }));
      setLoading(false);
      navigate(
        buildNavPath(`/form_submission/candidate/${CandidateCheckScreens.SUCCESS}/${token}`),
      );
    } else {
      setLoading(false);
      navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
    }
  };

  return (
    <CandidatePage loading={loading} token={token} withTopLogo brand={brand}>
      <CandidatePage.View>
        <CandidatePage.Card className="flex flex-col gap-12">
          <div className="flex flex-col gap-8">
            <IconComponent icon={Database01Icon} className={styles.icon} size="xxlarge" />
            <h1 className="text-5xl primary-text u-font-weight-500 u-margin-0">{form?.name}</h1>
            {dueDate && (
              <span className="flex items-center gap-2 secondary-text u-font-size-small u-font-weight-400">
                <StatusPill
                  text={`Complete by ${moment(dueDate).format('DD/MM/YYYY')}`}
                  status="warning"
                />
              </span>
            )}
            {subCheck?.details?.message && (
              <FormMessage
                type={MessageType.Info}
                message={subCheck.details.message}
                closeable={false}
                noMargin
              />
            )}
          </div>
          <div className="border-b border-neutral-4" />
          <Form
            candidateId={candidate.id}
            form={form}
            rules={form?.rules}
            brand={brand}
            textParams={{
              candidate: candidate.first_name,
              candidateFirstName: candidate.first_name,
              candidateFullName: candidate.name,
              candidateLastName: candidate.last_name,
              job: candidate?.job_role,
              companyName: brand.brand_name || company_name,
            }}
            onSubmit={onSubmit}
            getCandidateUpload={getUpload}
            deleteCandidateUpload={deleteUpload}
            signCandidateUpload={signUpload}
            createCandidateUpload={createUpload}
          />
        </CandidatePage.Card>
      </CandidatePage.View>
    </CandidatePage>
  );
};

export default CandidateQuestionnaire;
